class TdOriginChecker extends HTMLElement {
  constructor() {
    super();
    this.COOKIE_NAME = "TDCookie";
    this.COOKIE_VALUE = "here";
    this.EXPIRY_DAYS = 30;
    this.CURRENT_PAGE_HANDLE = window.location.pathname.split("/").pop();
    this.URL_PARAMS = new URLSearchParams(window.location.search);
    this.REFERRAL_SOURCE = document.referrer;
    this.CHILD_SEGMENTS = document.querySelectorAll(".announcement-bar__item");
    this.ROTATION_TIMEOUT = parseInt(this.dataset.rotationTimeout || "5000", 10); // Default 5 seconds
    this.visibleBanners = [];
    this.currentBannerIndex = 0;
    this.rotationTimer = null;
  }

  connectedCallback() {
    this.displayNextBanner();
    this.checkAnyBannerDisplayed();
    this.updateComponentHeight();
    this.startRotation();
    window.addEventListener("resize", () => this.updateComponentHeight());
  }

  disconnectedCallback() {
    this.stopRotation();
  }

  // Publishes the bar height for use by the theme
  updateComponentHeight() {
    const height = this.clientHeight;
    document.documentElement.style.setProperty(
      "--announcement-bar-height",
      `${height}px`
    );
  }

  // Method to get cookie by name
  getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  // Check & set cookie
  checkAndSetCookie() {
    let isCookieSet = false;

    // Check if the cookie exists
    if (this.getCookie(this.COOKIE_NAME)) {
      isCookieSet = true;
    } else {
      // Cookie doesn't exist, so add it
      let expires = "";
      if (this.EXPIRY_DAYS) {
        const date = new Date();
        date.setTime(date.getTime() + this.EXPIRY_DAYS * 24 * 60 * 60 * 1000);
        expires = "; expires=" + date.toUTCString();
      }
      document.cookie = `${this.COOKIE_NAME}=${this.COOKIE_VALUE}${expires}; path=/`;
    }

    return isCookieSet;
  }

  // Function to check and display next banner
  displayNextBanner() {
    const isCookieSet = this.checkAndSetCookie();
    this.visibleBanners = [];

    for (let i = 0; i < this.CHILD_SEGMENTS.length; i++) {
      const item = this.CHILD_SEGMENTS[i];
      const utmType = item.dataset.utmType;
      const utmTag = this.URL_PARAMS.get(utmType);
      const displayPage = item.dataset.displayPage;
      const displayUtmTag = item.dataset.displayUtmTag;
      const displaySource = item.dataset.displaySource;
      const displayAll = item.dataset.displayAll;

      let shouldDisplay = false; // Start with the assumption the banner should NOT display
      let pageMatch = false;

      if (displayPage == "") {
        pageMatch = true;
      } else if (
        displayPage &&
        this.CURRENT_PAGE_HANDLE.includes(displayPage)
      ) {
        pageMatch = true;
      }

      // Logic Checks - Set shouldDisplay to true ONLY if MATCH
      if (displayUtmTag && pageMatch && utmTag === displayUtmTag) {
        shouldDisplay = true;
      }

      if (
        displaySource &&
        pageMatch &&
        this.REFERRAL_SOURCE === displaySource
      ) {
        shouldDisplay = true;
      }

      if (displayAll == "true") {
        shouldDisplay = true;
      }

      if (shouldDisplay) {
        // Add cookie-based class
        if (isCookieSet) {
          item.classList.add("td-show-returning");
          console.log("Cookie is already set.");
        } else {
          item.classList.add("td-show-new");
          console.log("Cookie was not set, but it is now.");
        }
        // Store visible banner for rotation
        this.visibleBanners.push(item);
      }
    }

    // Show first banner if we have any (skip transition on initial load)
    if (this.visibleBanners.length > 0) {
      this.showBannerAtIndex(0, true);
    }
  }

  // Show banner at specific index and hide others (with text fade transition)
  showBannerAtIndex(index, skipTransition = false) {
    if (!this.visibleBanners[index]) {
      return;
    }

    const currentBanner = this.visibleBanners[this.currentBannerIndex];
    const nextBanner = this.visibleBanners[index];

    // If skipping transition (initial load), show immediately
    if (skipTransition) {
      // Hide all banners
      this.visibleBanners.forEach((banner) => {
        banner.setAttribute("hidden", "");
        banner.classList.remove("td-fade-out");
      });

      // Show the target banner
      nextBanner.removeAttribute("hidden");
      this.currentBannerIndex = index;
      this.updateComponentHeight();
      return;
    }

    // Fade out current banner text, then fade in next banner text
    if (currentBanner && currentBanner !== nextBanner) {
      // Start fade-out on current banner text (keep banner visible for transition)
      currentBanner.classList.add("td-fade-out");

      // Wait for fade-out to complete
      setTimeout(() => {
        // Hide current banner completely
        currentBanner.setAttribute("hidden", "");
        currentBanner.classList.remove("td-fade-out");

        // Show next banner and trigger text fade-in
        nextBanner.removeAttribute("hidden");
        // Start text invisible for transition
        nextBanner.classList.add("td-fade-out");
        
        // Use requestAnimationFrame to ensure transition triggers
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            nextBanner.classList.remove("td-fade-out");
            this.currentBannerIndex = index;
            this.updateComponentHeight();
          });
        });
      }, 400); // Match CSS transition duration
    } else {
      // No current banner, just show the next one
      this.visibleBanners.forEach((banner) => {
        if (banner !== nextBanner) {
          banner.setAttribute("hidden", "");
        }
      });
      nextBanner.removeAttribute("hidden");
      this.currentBannerIndex = index;
      this.updateComponentHeight();
    }
  }

  // Rotate to next banner
  rotateToNextBanner() {
    if (this.visibleBanners.length <= 1) {
      return; // No need to rotate if 0 or 1 banner
    }

    const nextIndex = (this.currentBannerIndex + 1) % this.visibleBanners.length;
    this.showBannerAtIndex(nextIndex);
  }

  // Start rotation timer
  startRotation() {
    this.stopRotation(); // Clear any existing timer

    if (this.visibleBanners.length > 1) {
      this.rotationTimer = setInterval(() => {
        this.rotateToNextBanner();
      }, this.ROTATION_TIMEOUT);
    }
  }

  // Stop rotation timer
  stopRotation() {
    if (this.rotationTimer) {
      clearInterval(this.rotationTimer);
      this.rotationTimer = null;
    }
  }

  // Check if the entire section should be hidden
  checkAnyBannerDisplayed() {
    let anyBannerDisplayed = false;
    for (const item of this.CHILD_SEGMENTS) {
      if (!item.hasAttribute("hidden")) {
        anyBannerDisplayed = true;
        break;
      }
    }

    if (!anyBannerDisplayed) {
      const bannerSection = document.querySelector(".announcement-bar");
      if (bannerSection) {
        bannerSection.style.display = "none";
      }
    }
  }
}

customElements.define("td-origin-checker", TdOriginChecker);
